'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { DATABASE_MODE_TYPE_ASI } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/sidebar-recent-items-state/recentItemsTypes.js';
import { useCurrentResultIds, useThreadMetadata, } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { limitInfoFromNumber, limitInfoFromRateLimit, state, ThreadConfigContext, } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useInitialConfiguredModel } from '#pplx/frontend/apps/ask/providers/useInitialConfiguredModel.js';
import { useUnifiedComputerComposerEnabled } from '#pplx/frontend/apps/ask/providers/useUnifiedComputerComposerEnabled.js';
import { usePreferredSearchModelForSearchMode } from '#pplx/frontend/apps/ask/search/hooks/usePreferredSearchModelForSearchMode.js';
import { useAskDeepResearchDraftSelection, useInputActions, } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { useRateLimits } from '#pplx/frontend/apps/ask/user-settings/hooks/useRateLimits.js';
import { useUserSettingsQuery } from '#pplx/frontend/apps/ask/user-settings/hooks/useUserSettingsQuery.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { MAX_COMPARE_MODELS, isComputerModel, isSearchModel, SearchMode, SearchModel, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
export const ThreadConfigProvider = ({ children, shouldFetchSettings, initialConfiguredModelOverride, initialConfiguredModelOverrideIsLoading = false, }) => {
    const reason = 'thread-settings-provider';
    const serverSettings = useUserSettingsQuery({
        enabled: shouldFetchSettings,
        reason,
    });
    const { isMax, isEnterprise } = useIsSubscribed();
    const draftSelection = useAskDeepResearchDraftSelection();
    const { setAskDeepResearchDraftSelection } = useInputActions();
    const { currentThreadId, currentStreamId } = useCurrentResultIds();
    const threadMetadata = useThreadMetadata();
    const pathname = usePathname();
    // Project inputs have their own initial model policy and draft scope.
    const restoredDraftSelection = initialConfiguredModelOverride === undefined &&
        !currentThreadId &&
        !currentStreamId &&
        !isEnterprise
        ? draftSelection
        : undefined;
    const { getModeRateLimit, loaded: rateLimitsLoaded } = useRateLimits({
        enabled: shouldFetchSettings,
        reason: 'thread_settings_provider_mount',
    });
    const { model: defaultInitialConfiguredModel, isLoading: defaultInitialConfiguredModelIsLoading, } = useInitialConfiguredModel();
    const { getModelMode, isLoaded: isSearchModelsLoaded, computer_harness, } = useSearchModels({
        reason: 'thread-settings-provider',
    });
    const unifiedComputerComposerEnabled = useUnifiedComputerComposerEnabled(computer_harness);
    const { getPreferredSearchModelForSearchMode } = usePreferredSearchModelForSearchMode();
    const initialConfiguredModel = initialConfiguredModelOverride ?? defaultInitialConfiguredModel;
    const initialConfiguredModelIsLoading = initialConfiguredModelOverrideIsLoading ||
        (initialConfiguredModelOverride == null && defaultInitialConfiguredModelIsLoading);
    const convertedLimits = useMemo(() => ({
        proLimit: limitInfoFromRateLimit(getModeRateLimit('pro_search')),
        researchLimit: limitInfoFromRateLimit(getModeRateLimit('research')),
        labsLimit: limitInfoFromRateLimit(getModeRateLimit('labs')),
        agenticResearchLimit: limitInfoFromRateLimit(getModeRateLimit('agentic_research')),
        modelSpecificLimits: {},
    }), [getModeRateLimit]);
    // User's explicit model selection. When null, configuredModel falls through
    // to initialConfiguredModel (which reacts to async org data automatically).
    const [modelOverride, setModelOverride] = useState(null);
    const [selectedAskDeepResearchModel, setSelectedAskDeepResearchModel] = useState(null);
    const [showAskDeepResearchEducation, setShowAskDeepResearchEducation] = useState(false);
    const setAskDeepResearchModel = useCallback((model) => {
        setSelectedAskDeepResearchModel(model);
        setShowAskDeepResearchEducation(model !== null);
    }, []);
    const [threadConfig, setThreadConfig] = useState({
        ...state,
        gpt4Limit: limitInfoFromNumber(convertedLimits.proLimit.exactCount ?? 0),
        pplxAlphaLimit: limitInfoFromNumber(convertedLimits.researchLimit.exactCount ?? 0),
        pplxBetaLimit: limitInfoFromNumber(convertedLimits.labsLimit.exactCount ?? 0),
        agenticResearchLimit: limitInfoFromNumber(convertedLimits.agenticResearchLimit.exactCount ?? 0),
        modelSpecificLimits: convertedLimits.modelSpecificLimits ?? {},
        configuredModel: initialConfiguredModel,
        compareModels: [],
        exhaustedSearchMode: undefined,
    });
    /**
     * Updates the configured model to run the search against in thread
     * settings.
     */
    const setConfiguredModel = useCallback((configuredModel) => {
        setModelOverride(configuredModel);
        if (!isComputerModel(configuredModel)) {
            setAskDeepResearchModel(null);
            if (restoredDraftSelection)
                setAskDeepResearchDraftSelection(undefined);
        }
        else if (restoredDraftSelection) {
            setSelectedAskDeepResearchModel(restoredDraftSelection.researchModel);
            if (configuredModel !== restoredDraftSelection.computerModel) {
                setAskDeepResearchDraftSelection({
                    ...restoredDraftSelection,
                    computerModel: configuredModel,
                });
            }
        }
    }, [restoredDraftSelection, setAskDeepResearchDraftSelection, setAskDeepResearchModel]);
    const setSources = useCallback((sources) => {
        setThreadConfig((config) => {
            return { ...config, sources };
        });
    }, []);
    const resetSourcesAndModel = useCallback(() => {
        setSources(['web']);
        setModelOverride(null);
        setAskDeepResearchModel(null);
    }, [setSources, setAskDeepResearchModel]);
    const resetThreadConfig = useCallback(() => {
        resetSourcesAndModel();
    }, [resetSourcesAndModel]);
    const resetThreadConfigForResultChange = useCallback((nextThreadId, nextStreamId) => {
        // Transient submit window: useAskInput clears result ids before submit;
        // streamId is set before threadId arrives. Skip the reset entirely so the
        // user's explicit model selection is preserved through the in-flight submit.
        if (nextThreadId === undefined && nextStreamId !== undefined) {
            return;
        }
        resetSourcesAndModel();
    }, [resetSourcesAndModel]);
    // Reset sources and search mode when switching away from a thread so homepage
    // ask input state does not inherit thread-local choices. Thread result-derived
    // model restoration lives in the mounted thread input scope.
    const prevThreadIdRef = useRef(currentThreadId);
    // This layout effect is a commit-ordering barrier, not DOM measurement or paint work.
    // The parent must clear stale thread state before Home descendants' passive effects
    // consume explicit navigation intent such as qmode or qsources. A passive reset runs
    // after those descendants and overwrites their newly applied model or sources.
    useLayoutEffect(() => {
        const prevThreadId = prevThreadIdRef.current;
        prevThreadIdRef.current = currentThreadId;
        if (prevThreadId !== undefined && prevThreadId !== currentThreadId) {
            resetThreadConfigForResultChange(currentThreadId, currentStreamId);
        }
    }, [currentThreadId, currentStreamId, resetThreadConfigForResultChange]);
    const setCompareModels = useCallback((models) => {
        const limitedModels = models.slice(0, MAX_COMPARE_MODELS);
        setThreadConfig((config) => ({
            ...config,
            compareModels: limitedModels,
        }));
    }, []);
    const setExhaustedSearchMode = useCallback((mode) => {
        setThreadConfig((config) => ({
            ...config,
            exhaustedSearchMode: mode,
        }));
    }, []);
    useEffect(() => {
        if (serverSettings.createLimit !== null) {
            setThreadConfig((config) => ({
                ...config,
                createLimit: serverSettings.createLimit,
            }));
        }
    }, [serverSettings.createLimit]);
    useEffect(() => {
        if (serverSettings.uploadLimit !== null && serverSettings.uploadLimit !== undefined) {
            setThreadConfig((config) => ({
                ...config,
                uploadRateLimit: serverSettings.uploadLimit,
            }));
        }
    }, [serverSettings.uploadLimit]);
    useEffect(() => {
        if (!convertedLimits) {
            return;
        }
        setThreadConfig((config) => ({
            ...config,
            gpt4Limit: convertedLimits.proLimit,
            pplxAlphaLimit: convertedLimits.researchLimit,
            pplxBetaLimit: convertedLimits.labsLimit,
            agenticResearchLimit: convertedLimits.agenticResearchLimit,
            modelSpecificLimits: convertedLimits.modelSpecificLimits,
        }));
    }, [
        convertedLimits,
        threadConfig.gpt4Limit.available,
        threadConfig.pplxAlphaLimit.available,
        threadConfig.pplxBetaLimit.available,
        threadConfig.agenticResearchLimit.available,
    ]);
    /**
     * Resets the user's preferred search mode.
     *
     * Used when limits have been reached, search modes are disabled for the
     * organization, or other misc situations.
     */
    const resetConfiguredSearchModel = useCallback(() => {
        setConfiguredModel(SearchModel.DEFAULT);
    }, [setConfiguredModel]);
    // Restore the mode with its draft before automatic mode suggestions can run.
    const selectedConfiguredModel = modelOverride ?? restoredDraftSelection?.computerModel ?? initialConfiguredModel;
    const isFreshComposer = !currentThreadId && !currentStreamId && (pathname === '/' || pathname === '/computer');
    const isComputerThread = currentThreadId !== undefined && threadMetadata?.mode === String(DATABASE_MODE_TYPE_ASI);
    const preferComputer = unifiedComputerComposerEnabled && (isFreshComposer || isComputerThread);
    const preferredComputerModel = preferComputer
        ? getPreferredSearchModelForSearchMode(SearchMode.ASI)
        : undefined;
    const computerModel = isSearchModel(preferredComputerModel) && isComputerModel(preferredComputerModel)
        ? preferredComputerModel
        : SearchModel.ASI;
    const configuredModel = preferComputer && !isComputerModel(selectedConfiguredModel)
        ? computerModel
        : selectedConfiguredModel;
    const hideSearchModeToggle = preferComputer;
    const askDeepResearchModel = selectedAskDeepResearchModel ?? restoredDraftSelection?.researchModel ?? null;
    // Ref to read configuredModel inside the rate-limit effect without
    // making it a dependency (which would re-run the effect on every selection)
    const configuredModelRef = useRef(configuredModel);
    configuredModelRef.current = configuredModel;
    /**
     * Resets thread configuration from loaded rate limits. This prevents
     * cases where search modes might be set for users that don't have access.
     * When RESEARCH mode is cleared for non-Max users, tracks the exhausted
     * mode for UI display of upgrade CTA.
     */
    useEffect(() => {
        if (!rateLimitsLoaded || !convertedLimits || !isSearchModelsLoaded) {
            return;
        }
        const proAvailable = convertedLimits.proLimit.available;
        const researchAvailable = convertedLimits.researchLimit.available;
        const agenticResearchAvailable = convertedLimits.agenticResearchLimit.available;
        const configuredModel = configuredModelRef.current;
        const currentMode = getModelMode(configuredModel);
        // Track exhausted mode before resetting (only for non-Max users who have upgrade path)
        const isResearchExhausted = currentMode === SearchMode.RESEARCH && !researchAvailable && !isMax;
        const isAgenticResearchExhausted = currentMode === SearchMode.AGENTIC_RESEARCH && !agenticResearchAvailable;
        if (isResearchExhausted) {
            setExhaustedSearchMode(SearchMode.RESEARCH);
        }
        else if (isAgenticResearchExhausted) {
            setExhaustedSearchMode(SearchMode.AGENTIC_RESEARCH);
        }
        const isProExhaustedSelectingUpgradeModel = ![SearchModel.DEFAULT, SearchModel.STUDY, SearchModel.ASI].includes(configuredModel) &&
            !proAvailable;
        if (isProExhaustedSelectingUpgradeModel || isResearchExhausted || isAgenticResearchExhausted) {
            resetConfiguredSearchModel();
        }
    }, [
        convertedLimits,
        resetConfiguredSearchModel,
        rateLimitsLoaded,
        isMax,
        setExhaustedSearchMode,
        getModelMode,
        isSearchModelsLoaded,
    ]);
    const decrementCreateLimit = useCallback(() => {
        const createLimit = Math.max(threadConfig.createLimit - 1, 0);
        setThreadConfig((threadConfig) => ({
            ...threadConfig,
            createLimit,
        }));
    }, [threadConfig.createLimit]);
    const setUploadRateLimit = useCallback((count = 1) => {
        setThreadConfig((threadConfig) => {
            if (threadConfig.uploadRateLimit == null)
                return threadConfig;
            return {
                ...threadConfig,
                uploadRateLimit: Math.max(threadConfig.uploadRateLimit - count, 0),
            };
        });
    }, []);
    const setModelSpecificLimits = (modelSpecificLimits) => {
        setThreadConfig((threadSettings) => ({
            ...threadSettings,
            modelSpecificLimits,
        }));
    };
    const value = {
        ...threadConfig,
        configuredModel,
        hideSearchModeToggle,
        askDeepResearchModel,
        showAskDeepResearchEducation,
        setAskDeepResearchModel,
        initialConfiguredModelIsLoading,
        setConfiguredModel,
        resetThreadConfig,
        setSources,
        decrementCreateLimit,
        setUploadRateLimit,
        setModelSpecificLimits,
        compareModels: threadConfig.compareModels,
        setCompareModels,
        exhaustedSearchMode: threadConfig.exhaustedSearchMode,
        setExhaustedSearchMode,
    };
    return _jsx(ThreadConfigContext.Provider, { value: value, children: children });
};
//# sourceMappingURL=ThreadSettingsProvider.js.map